import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ENTITY_TYPES,
  LocationDTO,
  EntityLocations,
  ISearchInput,
  ISearchResponse,
  searchInputToQueryString,
} from '@sales-center/common'
import { ServiceConfig } from '../services/config'
import { APIError, APIReject, get, post, put, remove } from '../services/crud'
import {
  BillTo,
  DeleteLocationDTO,
  LocationInput,
  LocationMatchInput,
  ServiceLocation,
} from './typings'
import {
  IBillToServiceLocationParams,
  IServiceLocationParams,
} from '../locations/typings'

import { updateEntityNameByLocationId } from '../leadOppProducts'
import { getLeadDetail } from '../lead'
import { getOpportunityDetail } from '../opportunity'

const baseUrl = ServiceConfig.baseAPIUrl

export const loadEntityLocations = createAsyncThunk<
  EntityLocations,
  {
    entityId: number
    entityType: ENTITY_TYPES
    updateExisting?: boolean
  },
  { rejectValue: APIReject }
>('entityLocations/load', async (input, thunkApi) => {
  const { entityId, entityType } = input

  const path =
    entityType === ENTITY_TYPES.Lead
      ? `${ServiceConfig.location.lead}/Lead`
      : `${ServiceConfig.location.opp}/Opportunity`

  const response = await get(`${baseUrl}/${path}/${entityId}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const addEditEntityLocation = createAsyncThunk<
  LocationDTO,
  LocationInput,
  { rejectValue: APIReject }
>('entityLocations/addEdit', async (input, thunkApi) => {
  const { id, entityType } = input

  const request = !!id ? put : post

  const path =
    entityType === ENTITY_TYPES.Lead
      ? ServiceConfig.location.lead
      : ServiceConfig.location.opp

  const response = await request(`${baseUrl}/${path}`, input)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteEntityLocation = createAsyncThunk<
  DeleteLocationDTO,
  { id: number; entityType: ENTITY_TYPES; entityId: number },
  { rejectValue: APIReject }
>('entityLocations/delete', async (input, thunkApi) => {
  const { id, entityType, entityId } = input

  const path =
    entityType === ENTITY_TYPES.Lead
      ? ServiceConfig.location.lead
      : ServiceConfig.location.opp

  const response = await remove(`${baseUrl}/${path}/${id}`)

  if (response.ok) {
    updateEntityNameByLocationId(entityType, entityId, thunkApi, 'delete', id)
    return await response.json()
  }

  return thunkApi.rejectWithValue(await response.json())
})

export const matchCreateNew = createAsyncThunk<
  void,
  { entityType: ENTITY_TYPES; data: LocationMatchInput; detail?: boolean },
  { rejectValue: APIReject }
>('entityLocations/matchCreateNew', async (input, thunkApi) => {
  const { entityType, data, detail } = input

  const response = await post(`${baseUrl}/${entityType}/match`, data)

  if (!response.ok) return thunkApi.rejectWithValue(await response.json())

  thunkApi.dispatch(
    loadEntityLocations({
      entityType,
      entityId: data.id,
      updateExisting: true,
    }),
  )

  if (detail) {
    if (entityType === ENTITY_TYPES.Lead) {
      thunkApi.dispatch(getLeadDetail(data.id))
    } else if (entityType === ENTITY_TYPES.Opportunity)
      thunkApi.dispatch(getOpportunityDetail(data.id))
  }
})

export const searchBillTos = createAsyncThunk<
  ISearchResponse<BillTo>,
  ISearchInput,
  { rejectValue: APIReject }
>('entityLocations/searchBillTos', async (searchInput, thunkApi) => {
  const query = searchInput.query ?? ''

  const formattedQuery =
    typeof query === 'string' ? query.toLowerCase().split(' or ') : query

  const qstring = searchInputToQueryString({
    ...searchInput,
    query: formattedQuery,
  })

  const response = await get(
    `${baseUrl}/${ServiceConfig.billTo.billTo}?${qstring}`,
  )

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const searchServiceLocations = createAsyncThunk<
  ISearchResponse<ServiceLocation>,
  ISearchInput,
  { rejectValue: APIReject }
>('entityLocations/searchServiceLocations', async (searchInput, thunkApi) => {
  const query = searchInput.query ?? ''

  const formattedQuery =
    typeof query === 'string' ? query.toLowerCase().split(' or ') : query

  const qstring = searchInputToQueryString({
    ...searchInput,
    query: formattedQuery,
  })

  const response = await get(
    `${baseUrl}${ServiceConfig.location.location}/core?${qstring}`,
  )

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getRelatedLocations = createAsyncThunk<
  ISearchResponse<ServiceLocation>,
  string,
  { rejectValue: APIReject }
>('entityLoations/relatedLocations', async (billToId, thunkApi) => {
  const response = await get(
    `${baseUrl}/${ServiceConfig.billTo.locations(billToId)}`,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getBillToDetails = createAsyncThunk<
  ISearchResponse<LocationDTO>,
  IBillToServiceLocationParams
>('locations/billTo', async (params, thunkApi) => {
  const { entityId, entityType, skip, take } = params

  const url =
    entityType === ENTITY_TYPES.Lead
      ? `${ServiceConfig.baseAPIUrl}/${ServiceConfig.location.lead}`
      : `${ServiceConfig.baseAPIUrl}/${ServiceConfig.location.opp}`

  const response = await get(
    `${url}/${entityId}/locations?skip=${skip}&take=${take}`,
  )

  if (response.ok) {
    const payload: ISearchResponse<LocationDTO> = await response.json()
    return payload
  } else {
    const json = await response.json()
    return thunkApi.rejectWithValue(json)
  }
})

export const addServiceLocations = createAsyncThunk<
  EntityLocations,
  IServiceLocationParams,
  { rejectValue: APIError }
>('locations/ServiceLocations', async (params, thunkApi) => {
  const { entityId, entityType, serviceLocations } = params

  const url =
    entityType === ENTITY_TYPES.Lead
      ? `${ServiceConfig.baseAPIUrl}/${ServiceConfig.location.lead}`
      : `${ServiceConfig.baseAPIUrl}/${ServiceConfig.location.opp}`

  const response = await post(
    `${url}/${entityId}/addLocations`,
    serviceLocations,
  )

  if (response.ok) {
    return await response.json()
  }

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export async function getLocationsWithPropertyDetails(
  entityId: number,
  entityType: ENTITY_TYPES,
): Promise<LocationDTO[]> {
  if (!entityId) throw new Error('Entity ID is required')

  const locationPath =
    entityType === ENTITY_TYPES.Lead
      ? ServiceConfig.location.lead
      : ServiceConfig.location.opp

  const url = `${ServiceConfig.baseAPIUrl}/${locationPath}/${entityId}/locationsWithPropertyDetails`

  const response = await get(url)

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error?.message || 'Failed to fetch location details')
  }

  return await response.json()
}
